<script setup lang="ts">
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { Action as A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Subject as S } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { onScopeDispose } from 'vue'
import { onAuthContextChanged } from '@/@vxapp/Auth/authContext'
import { computed, nextTick, onMounted, ref, shallowRef, watch } from 'vue'
import { can } from '@layouts/plugins/casl'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { useMessagesStore } from '@/@vxapp/Messages/useMessagesStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import MessageDialog from '@/components/Dialogs/Messages/MessageDialog.vue'
import MessagesDataTable from '@/components/Tables/Messages/MessagesDataTable.vue'

type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search'

definePage({ meta: PageMeta.Messages.List.Meta })
setPageTitle(PageMeta.Messages.List.Title)

const route = useRoute()
const router = useRouter()
const messageStore = useMessagesStore() as any
const messagesDataTable = ref<InstanceType<typeof MessagesDataTable> | null>(null)
const isMessageDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const searchQuery = ref('')
const resultCount = ref(0)
let internallySyncedRouteQueryKey: string | null = null

const defaultQueryParams: PaginationQueryParams = {
  ...PaginationField.QueryParams,
  hideInactive: undefined,
}

const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search']
const sortableFields = new Set(['id', 'sent_on', 'created_at'])
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Newest ID first', value: '-id' },
  { title: 'Oldest ID first', value: '+id' },
  { title: 'Latest sent first', value: '-sent_on' },
  { title: 'Earliest sent first', value: '+sent_on' },
  { title: 'Recently created', value: '-created_at' },
]

const canCompose = computed(
  () => can('create', PageMeta.Messages.Add.Meta.subject) && can('read', 'Messages:load_new')
)

const resultLabel = computed(() =>
  resultCount.value === 1 ? '1 communication' : `${resultCount.value} communications`
)

function normalizeQueryValue(value: unknown): string | undefined {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  if (typeof normalizedValue === 'number') return String(normalizedValue)

  return typeof normalizedValue === 'string' ? normalizedValue : undefined
}

function normalizePositiveInteger(value: unknown, fallback: number) {
  const normalizedValue = normalizeQueryValue(value)
  const parsedValue = normalizedValue ? Number.parseInt(normalizedValue, 10) : Number.NaN

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

function normalizeSort(value: unknown) {
  const sort = normalizeQueryValue(value)
  const sortField = sort?.replace(/^[+-]/, '')

  return sort && sortField && sortableFields.has(sortField) && /^[+-]/.test(sort)
    ? sort
    : defaultQueryParams.sort
}

function normalizeQueryParams(source: Record<string, unknown>): PaginationQueryParams {
  const requestedLimit = normalizePositiveInteger(source.limit, defaultQueryParams.limit)
  const search = normalizeQueryValue(source.search)?.trim()

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source.page, defaultQueryParams.page),
    limit: limitItems.includes(requestedLimit) ? requestedLimit : defaultQueryParams.limit,
    sort: normalizeSort(source.sort),
    search: search || undefined,
    hideInactive: undefined,
  }
}

function queryParamsMatch(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRouteQuery() {
  const currentQuery = normalizeQueryParams(messageStore.queryParams ?? defaultQueryParams)
  const routeQuery = normalizeQueryParams(route.query as Record<string, unknown>)

  searchQuery.value = routeQuery.search ?? ''

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  messageStore.queryParams = routeQuery

  return true
}

function buildRouteQuery(
  source: Record<string, unknown> = messageStore.queryParams ?? defaultQueryParams
) {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  const queryParams = normalizeQueryParams(source)

  return {
    ...nextQuery,
    ...(queryParams.page !== defaultQueryParams.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== defaultQueryParams.limit ? { limit: String(queryParams.limit) } : {}),
    ...(queryParams.sort !== defaultQueryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
  }
}

async function fetchMessages(requestedQuery?: PaginationQueryParams) {
  const normalizedRequestedQuery = normalizeQueryParams(
    requestedQuery ?? messageStore.queryParams ?? defaultQueryParams
  )

  await messagesDataTable.value?.fetchMessages(normalizedRequestedQuery)
}

async function syncRouteQuery(normalizedQueryParams: PaginationQueryParams) {
  const nextRouteQuery = buildRouteQuery({ ...normalizedQueryParams })
  const nextRouteQueryKey = JSON.stringify(nextRouteQuery)

  if (JSON.stringify(route.query) === nextRouteQueryKey) return

  internallySyncedRouteQueryKey = nextRouteQueryKey
  await router.replace({ query: nextRouteQuery })
}

async function updateQueryParams(patch: Partial<PaginationQueryParams>) {
  const nextQueryParams = normalizeQueryParams({ ...messageStore.queryParams, ...patch })

  messageStore.queryParams = nextQueryParams

  await syncRouteQuery(nextQueryParams)

  await nextTick()
  await fetchMessages(nextQueryParams)
}

async function handleSearch() {
  await updateQueryParams({ search: searchQuery.value.trim() || undefined, page: 1 })
}

function handleSearchInput(value: unknown) {
  searchQuery.value = String(value ?? '')
}

async function handleClear() {
  searchQuery.value = ''
  await updateQueryParams({ search: undefined, page: 1 })
}

async function handleSortChange(value: unknown) {
  await updateQueryParams({ sort: String(value), page: 1 })
}

async function handleLimitChange(value: unknown) {
  await updateQueryParams({ limit: Number(value), page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

function handleLoaded(entriesFound: number) {
  resultCount.value = entriesFound
}

async function handleCreateMessage() {
  if (!canCompose.value) return

  await messageStore.fetchTemplate()
  isMessageDialogVisible.value = true
}

onMounted(async () => {
  messageStore.resetDataTableState()
  await messageStore.setTableSkeleton()
  applyRouteQuery()
  isRouteQueryReady.value = true
  await nextTick()
  await fetchMessages()
})

watch(
  () => route.query,
  async () => {
    const routeQueryKey = JSON.stringify(route.query)
    const wasSyncedInternally = internallySyncedRouteQueryKey === routeQueryKey
    const requestedQuery = normalizeQueryParams(route.query as Record<string, unknown>)

    if (wasSyncedInternally) internallySyncedRouteQueryKey = null

    if (!isRouteQueryReady.value || wasSyncedInternally || !applyRouteQuery()) return

    await nextTick()
    await fetchMessages(requestedQuery)
  },
  { deep: true }
)
onScopeDispose(
  onAuthContextChanged(() => {
    isMessageDialogVisible.value = false
    resultCount.value = 0
    searchQuery.value = ''
  })
)

const canReadCount = () =>
  useAuthStore().getAbility()?.can(A.READ, S.MESSAGES_PREVIEW_ALL, 'paginationMeta.entriesFound') ??
  false
</script>

<template>
  <main
    class="messages-workspace"
    data-testid="messages-workspace"
  >
    <section
      class="messages-workspace__hero"
      data-testid="messages-hero"
    >
      <div
        class="messages-workspace__hero-icon"
        aria-hidden="true"
      >
        <VIcon icon="tabler-messages" />
      </div>
      <div class="messages-workspace__hero-copy">
        <span>Communications</span>
        <h1>Messages</h1>
        <p>Review delivery status, find a communication, or compose a new message.</p>
      </div>
      <div class="messages-workspace__hero-command">
        <div>
          <small>Current results</small>
          <strong v-if="canReadCount()">{{ resultLabel }}</strong>
        </div>
        <VBtn
          v-if="canCompose"
          color="primary"
          prepend-icon="tabler-edit"
          @click="handleCreateMessage"
        >
          Compose message
        </VBtn>
      </div>
    </section>

    <VCard class="messages-workspace__toolbar">
      <VCardText>
        <VForm
          class="messages-workspace__search"
          role="search"
          @submit.prevent="handleSearch"
        >
          <!-- vortex-validation-ignore-element: server search is non-validation filter UI -->
          <AppTextField
            :model-value="searchQuery"
            aria-label="Search messages"
            clearable
            hide-details
            placeholder="Search message subjects"
            prepend-inner-icon="tabler-search"
            vortex-validation-ignore-element
            @click:clear="handleClear"
            @update:model-value="handleSearchInput"
          />
          <VBtn
            color="primary"
            type="button"
            @click="handleSearch"
          >
            Search
          </VBtn>
        </VForm>
        <!-- vortex-validation-ignore-element: result sorting is non-validation UI -->
        <AppSelect
          :model-value="messageStore.queryParams?.sort ?? defaultQueryParams.sort"
          :items="sortItems"
          aria-label="Sort messages"
          hide-details
          label="Sort"
          vortex-validation-ignore-element
          @update:model-value="handleSortChange"
        />
        <!-- vortex-validation-ignore-element: page size is non-validation UI -->
        <AppSelect
          :model-value="messageStore.queryParams?.limit ?? defaultQueryParams.limit"
          :items="limitItems"
          aria-label="Messages per page"
          hide-details
          label="Per page"
          vortex-validation-ignore-element
          @update:model-value="handleLimitChange"
        />
      </VCardText>
    </VCard>

    <MessagesDataTable
      ref="messagesDataTable"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
    />
  </main>

  <MessageDialog
    v-if="isMessageDialogVisible"
    v-model:is-dialog-visible="isMessageDialogVisible"
    :message-data="messageStore.template"
    is-create
    :is-update="false"
    :is-loading="messageStore.isLoading"
  />
</template>

<style scoped lang="scss">
.messages-workspace {
  display: grid;
  min-inline-size: 0;
  color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
  gap: 1rem;
}

.messages-workspace__hero {
  display: grid;
  min-inline-size: 0;
  align-items: center;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 1rem;
  background:
    radial-gradient(circle at 92% 20%, rgba(var(--v-theme-primary), 0.1), transparent 16rem),
    rgb(var(--v-theme-surface));
  gap: 1rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  overflow: hidden;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
}

.messages-workspace__hero-icon {
  display: grid;
  block-size: 3.5rem;
  inline-size: 3.5rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.22);
  border-radius: 0.875rem;
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-size: 1.6rem;
  place-items: center;
}

.messages-workspace__hero-copy > span {
  color: rgb(var(--v-theme-primary));
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.messages-workspace__hero h1 {
  margin: 0.15rem 0 0.25rem;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(1.65rem, 3vw, 2.15rem);
  letter-spacing: -0.035em;
  line-height: 1.15;
}

.messages-workspace__hero p {
  max-inline-size: 42rem;
  margin: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.875rem;
}

.messages-workspace__hero-command {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.messages-workspace__hero-command > div {
  display: grid;
  gap: 0.1rem;
  text-align: end;
}

.messages-workspace__hero-command small {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.messages-workspace__hero-command strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 1rem;
  font-weight: 600;
}

.messages-workspace__hero-command :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 0.625rem;
}

.messages-workspace__toolbar {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 1rem;
  box-shadow: none;
}

.messages-workspace__toolbar :deep(.v-card-text) {
  display: grid;
  align-items: center;
  gap: 0.75rem;
  grid-template-columns: minmax(18rem, 1fr) minmax(11rem, 0.28fr) minmax(8rem, 0.18fr);
}

.messages-workspace__search {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.messages-workspace__toolbar :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 0.5rem;
}

@media (max-width: 959px) {
  .messages-workspace__hero {
    align-items: start;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .messages-workspace__hero-command {
    grid-column: 1 / -1;
    justify-content: space-between;
    border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding-block-start: 1rem;
    inline-size: 100%;
  }

  .messages-workspace__toolbar :deep(.v-card-text) {
    grid-template-columns: 1fr 1fr;
  }

  .messages-workspace__search {
    grid-column: 1 / -1;
  }
}

@media (max-width: 599px) {
  .messages-workspace__hero {
    padding: 1rem;
  }

  .messages-workspace__toolbar :deep(.v-card-text),
  .messages-workspace__search {
    grid-template-columns: 1fr;
  }

  .messages-workspace__toolbar :deep(.v-btn) {
    inline-size: 100%;
  }

  .messages-workspace__hero-command {
    display: grid;
  }

  .messages-workspace__hero-command > div {
    text-align: start;
  }

  .messages-workspace__hero-command :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
